import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'src/components/muiTheme/utils';
import { CartItem as CartItemType } from 'src/models/cart/_types';
import { Section } from 'src/shared/SidebarDialog';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { Identifier, WithSearchResult } from 'src/types/utilityTypes';

// Type for POS selected items (not CartItems)
export interface PosSelectedItem {
  id: Identifier;
  name: string;
  type: string;
  itemId: Identifier;
  itemType: string;
  description?: string;
  paymentOptions?: any[];
  quantity: number;
}

interface UsePosConfigItemSelectorProps {
  type: 'pos' | 'kiosk';
  onSave: (items: PosSelectedItem[]) => void;
  onClose: () => void;
  isMobile: boolean;
}

export const usePosConfigItemSelector = ({
  type,
  onSave,
  onClose,
  isMobile: _isMobile, // Reserved for mobile-specific behavior
}: UsePosConfigItemSelectorProps) => {
  const [selectedItemType, setSelectedItemType] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [posItems, setPosItems] = useState<PosSelectedItem[]>([]);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [debouncedItemSearchValue, setDebouncedItemSearchValue] = useState('');

  // Create the debounced function using the same pattern as PaymentDialogContext
  const debouncedSetItemSearch = useMemo(
    () => debounce((value: string) => setDebouncedItemSearchValue(value), 300),
    []
  );

  useEffect(() => {
    debouncedSetItemSearch(itemSearchValue);
    return () => {
      debouncedSetItemSearch.clear();
    };
  }, [itemSearchValue, debouncedSetItemSearch]);

  const handleItemClick = (itemType: string, section: string) => {
    setSelectedItemType(itemType);
    setSelectedSection(section);
    // Reset item search when switching types
    setItemSearchValue('');
  };

  const handleSelectItem = async (
    item: WithSearchResult<any>
  ): Promise<void> => {
    // Add item to pos items - spread the item and add quantity
    const posItem = {
      ...item,
      quantity: 1, // Default quantity
    } as PosSelectedItem;
    setPosItems((prev) => [...prev, posItem]);
  };

  const handleRemoveItem = (item: CartItemType, index?: number) => {
    // Remove item from pos items
    if (index !== undefined) {
      setPosItems((prev) => prev.filter((_, i) => i !== index));
    } else {
      setPosItems((prev) => prev.filter((posItem) => posItem.id !== item.id));
    }
  };

  const handleSaveItems = () => {
    onSave(posItems);
    setPosItems([]); // Clear selected items after saving
    onClose();
  };

  const isKiosk = type === 'kiosk';

  const selectItemTypeHeader = selectedItemType
    ? selectableItemTypes.find(
        (itemType) => itemType.value === selectedItemType
      )?.label
    : 'POS Item Selector';

  const sections: Section[] = [
    {
      title: 'Library',
      items: isKiosk
        ? [
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Library',
              onClick: () => handleItemClick('product', 'Library'),
            },
          ]
        : [
            {
              label: 'Packages',
              icon: icon({ name: 'box', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'package' && selectedSection === 'Library',
              onClick: () => handleItemClick('package', 'Library'),
            },
            {
              label: 'Groups',
              icon: icon({ name: 'users', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'group' && selectedSection === 'Library',
              onClick: () => handleItemClick('group', 'Library'),
            },
            {
              label: 'Plans',
              icon: icon({ name: 'list', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'plan' && selectedSection === 'Library',
              onClick: () => handleItemClick('plan', 'Library'),
            },
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Library',
              onClick: () => handleItemClick('product', 'Library'),
            },
          ],
    },
    {
      title: 'Finders',
      items: isKiosk
        ? [
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Finders',
              onClick: () => handleItemClick('product', 'Finders'),
            },
          ]
        : [
            {
              label: 'Packages',
              icon: icon({ name: 'box', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'package' && selectedSection === 'Finders',
              onClick: () => handleItemClick('package', 'Finders'),
            },
            {
              label: 'Groups',
              icon: icon({ name: 'users', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'group' && selectedSection === 'Finders',
              onClick: () => handleItemClick('group', 'Finders'),
            },
            {
              label: 'Plans',
              icon: icon({ name: 'list', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'plan' && selectedSection === 'Finders',
              onClick: () => handleItemClick('plan', 'Finders'),
            },
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Finders',
              onClick: () => handleItemClick('product', 'Finders'),
            },
          ],
    },
  ];

  const checkIfItemAdded = (item: any) => {
    return !!posItems.find((i) => {
      return i.id === item.id && i.type === item.type;
    });
  };

  const handleBackClick = () => {
    setSelectedItemType(null);
    setSelectedSection(null);
    setItemSearchValue('');
  };

  return {
    // State
    selectedItemType,
    selectedSection,
    posItems,
    itemSearchValue,
    debouncedItemSearchValue,

    // Computed values
    selectItemTypeHeader,
    sections,
    isKiosk,

    // Handlers
    handleItemClick,
    handleSelectItem,
    handleRemoveItem,
    handleSaveItems,
    handleBackClick,
    setItemSearchValue,
    checkIfItemAdded,
  };
};
